<template>
    <div class="noti-panel">

        <div class="noti-panel__header">
            <div class="noti-panel__title">
                <span class="noti-panel__title-icon">
                    <i class="pi pi-bell" />
                </span>
                <span>Notifications</span>
                <span v-if="unreadCount > 0" class="noti-panel__badge">{{ unreadCount }}</span>
            </div>
            <Button
                v-if="unreadCount > 0"
                label="Mark as all read"
                class="p-button-text p-button-sm noti-panel__mark-all-btn"
                :loading="markingAll"
                @click="markAllAsRead"
            />
            <span v-else class="noti-panel__all-read-label">
                <i class="pi pi-check-circle" style="color: var(--green-500); font-size: 0.85rem;" />
                All read
            </span>
        </div>

        <!-- Loading -->
        <div v-if="loading && notifications.length === 0" class="noti-panel__state-view">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.4rem; color: var(--primary-color);" />
            <span>Loading…</span>
        </div>

        <!-- Empty -->
        <div v-else-if="notifications.length === 0" class="noti-panel__state-view">
            <span class="noti-panel__empty-icon-wrap">
                <i class="pi pi-bell" />
            </span>
            <p>No notifications yet</p>
            <small>You're all caught up!</small>
        </div>

        <!-- List -->
        <ul v-else class="noti-panel__list">
            <li
                v-for="noti in notifications"
                :key="noti.eventId"
                class="noti-panel__item"
                :class="{ 'is-unread': !noti.userAckFlag, 'is-read': noti.userAckFlag }"
                @click="onItemClick(noti)"
            >
                <!-- Unread accent bar -->
                <span v-if="!noti.userAckFlag" class="noti-panel__accent-bar" />

                <!-- Icon -->
                <span class="noti-panel__item-icon" :class="{ 'is-read-icon': noti.userAckFlag }">
                    <i :class="resolveEventIcon(noti.eventType)" />
                </span>

                <!-- Body -->
                <div class="noti-panel__item-body">
                    <h5 class="noti-panel__item-title">{{ noti.eventTitle }}</h5>
                    <p class="noti-panel__item-msg">{{ noti.eventMessage }}</p>
                    <small class="noti-panel__item-time">{{ relativeTime(noti.recordedDate) }}</small>
                </div>

                <!-- Unread dot -->
                <span v-if="!noti.userAckFlag" class="noti-panel__unread-dot" />
            </li>

            <!-- View more -->
            <li v-if="hasNextPage" class="noti-panel__load-more">
                
                <Button
                    label="View more"
                    icon="pi pi-chevron-down"
                    iconPos="right"
                    class="p-button-text p-button-sm p-button-secondary noti-panel__load-more-btn"
                    :loading="loading"
                    @click="fetchNextPage"
                />
                
            </li>
        </ul>

        <!-- Footer  -->
        <div
            v-if="notifications.length > 0 && !hasNextPage"
            class="noti-panel__footer"
        >
            <span class="noti-panel__footer-label">
                <!-- <i class="pi pi-list" /> -->
                You're all caught up!
            </span>
        </div>

    </div>
</template>

<script>
import { useToast } from 'primevue/usetoast';

const EVENT_TYPE_ICONS = {
    OPR: 'pi pi-box',
    REQ: 'pi pi-shopping-bag',
    PNT: 'pi pi-gift',
    CPT: 'pi pi-tag',
    CSH: 'pi pi-wallet',
};

export default {
    name: 'NotificationPanel',

    setup() {
        const toast = useToast();
        return { toast };
    },

    props: {
        notiService: { type: Object, required: true },
        refId:       { type: String, default: null },
    },

    emits: ['update:unreadCount'],

    data() {
        return {
            notifications: [],
            loading:      false,
            markingAll:   false,
            currentPage:  1,
            hasNextPage:  false,
            totalPages:   1,
        };
    },

    computed: {
        unreadCount() {
            return this.notifications.filter(n => !n.userAckFlag).length;
        },
    },

    watch: {
        unreadCount(count) {
            this.$emit('update:unreadCount', count);
        },
    },

    methods: {
        /** Called by parent via ref */
        refresh() {
            this.currentPage   = 1;
            this.notifications = [];
            this.fetchNotifications(1);
        },

        async fetchNotifications(page = 1) {
            if (!this.refId) return;
            this.loading = true;
            try {
                await this.$verifyUserData(this);
                const response = await this.notiService.fetchNotifications(this.refId, page);

                if (page === 1) {
                    this.notifications = response.items || [];
                } else {
                    this.notifications = [...this.notifications, ...(response.items || [])];
                }

                this.currentPage = response.pageNumber;
                this.hasNextPage = response.hasNextPage;
                this.totalPages  = response.totalPages;
            } catch {
                this.toast.add({
                    severity: 'error',
                    summary:  'Notifications',
                    detail:   'Failed to load notifications.',
                    life:     3500,
                });
            } finally {
                this.loading = false;
            }
        },

        fetchNextPage() {
            if (this.hasNextPage && !this.loading) {
                this.fetchNotifications(this.currentPage + 1);
            }
        },

        async onItemClick(noti) {
            if (noti.userAckFlag) return;
            try {
                await this.notiService.acknowledgeEvent(noti.eventId, this.refId);
                noti.userAckFlag = true;
            } catch {
                console.warn('Failed to acknowledge notification:', noti.eventId);
            }
        },

        async markAllAsRead() {
            const unread = this.notifications.filter(n => !n.userAckFlag);
            if (!unread.length) return;
            this.markingAll = true;
            try {
                await Promise.all(unread.map(n => this.notiService.acknowledgeEvent(n.eventId, this.refId)));
                this.notifications.forEach(n => (n.userAckFlag = true));
            } catch {
                this.toast.add({ severity: 'error', summary: 'Notifications', detail: 'Could not mark all as read.', life: 3500 });
            } finally {
                this.markingAll = false;
            }
        },

        resolveEventIcon(type) {
            return EVENT_TYPE_ICONS[type] || 'pi pi-bell';
        },

        relativeTime(dateStr) {
            if (!dateStr) return '';
            const diffMs = Date.now() - new Date(dateStr).getTime();
            const mins   = Math.floor(diffMs / 60000);
            if (mins < 1)  return 'just now';
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24)  return `${hrs}h ago`;
            return `${Math.floor(hrs / 24)}d ago`;
        },
    },
};
</script>

<style lang="scss" scoped>

.noti-panel {
    width: 380px;
    max-height: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;           
    border-radius: 12px;

    @media screen and (max-width: 895px) {
        width: calc(100vw - 32px);
        max-height: 72vh;
    }
}


.noti-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 12px;
    border-bottom: 1px solid var(--surface-200);
    flex-shrink: 0;
    background: var(--surface-0);
}

.noti-panel__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 1rem;
    color: var(--text-color);
}

.noti-panel__title-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: var(--primary-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.85rem;
    flex-shrink: 0;
}

.noti-panel__badge {
    background: var(--yellow-400);
    color: #000;
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.noti-panel__mark-all-btn {
    font-size: 1rem !important;
    padding: 3px 8px !important;
    white-space: nowrap;
    text-decoration: underline;
}

.noti-panel__all-read-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.78rem;
    color: var(--text-color-secondary);
}

/* State views (loading / empty)── */
.noti-panel__state-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 44px 16px;
    gap: 10px;
    color: var(--text-color-secondary);
    font-size: 0.88rem;

    p     { margin: 0; font-weight: 600; color: var(--text-color); }
    small { font-size: 0.78rem; }
}

.noti-panel__empty-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--surface-100);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: 1.6rem;
        color: var(--surface-400);
    }
}


.noti-panel__list {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    flex: 1;

    /* Custom scrollbar */
    &::-webkit-scrollbar       { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: var(--surface-300); border-radius: 4px; }
}


.noti-panel__item {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 13px 16px 13px 20px;
    cursor: pointer;
    border-bottom: 1px solid var(--surface-200);
    transition: background-color 0.15s;

    &:last-child { border-bottom: none; }


    &.is-read {
        background: var(--surface-0);

        &:hover { background: var(--surface-50); }

        .noti-panel__item-msg {
            color: var(--text-color-secondary);
            font-weight: 400;
        }
    }

    &.is-unread {
        background: color-mix(in srgb, var(--primary-color) 6%, var(--surface-0));

        &:hover {
            background: color-mix(in srgb, var(--primary-color) 10%, var(--surface-0));
        }

        .noti-panel__item-title {
            font-weight: 600;
            color: var(--text-color);
        }

        

        .noti-panel__item-msg {
            font-weight: 600;
            color: var(--text-color);
        }
    }
}


.noti-panel__accent-bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--primary-color);
    border-radius: 0 3px 3px 0;
}


.noti-panel__unread-dot {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary-color);
    flex-shrink: 0;
    box-shadow: 0 0 0 2px var(--surface-0);
}

.noti-panel__item-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.9rem;
    margin-top: 1px;
    transition: background 0.15s, opacity 0.15s;

    &.is-read-icon {
        background: var(--surface-200);
        color: var(--text-color-secondary);
    }
}


.noti-panel__item-body {
    flex: 1;
    min-width: 0;
    padding-right: 12px;    
}

.noti-panel__item-title {
    margin: 0 0 4px;
    font-size: 1.1rem;
    line-height: 1.45;
    word-break: break-word;
}

.noti-panel__item-msg {
    margin: 0 0 4px;
    font-size: 1rem;
    line-height: 1.45;
    word-break: break-word;
}

.noti-panel__item-time {
    font-size: 0.72rem;
    color: var(--text-color-secondary);
}


.noti-panel__load-more {
    display: flex;
    justify-content: center;
    padding: 6px 16px;
    border-top: 1px solid var(--surface-100);
    background: var(--surface-0);
}

.noti-panel__load-more-btn {
    width: 100%;
    justify-content: center;
}


.noti-panel__footer {
    flex-shrink: 0;
    padding: 10px 16px;
    text-align: center;
    border-top: 1px solid var(--surface-100);
    background: var(--surface-0);
}

.noti-panel__footer-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    cursor: default;
    user-select: none;

    i { font-size: 0.78rem; }
}
</style>